"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { BankBadge } from "@/components/banks/BankBadge";
import { TradeDossier } from "@/components/sign/TradeDossier";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 5.18 — T3 reveal. The reputation arc compounds across two
 * jurisdictions on a reverse-corridor trade. Pampagro and Gulf Phosphate
 * both gain a verified trade; Pampagro's bank-partner list adds Itaú.
 */
export type ReputationDelta = {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

export function T3Reveal({
  pampagro,
  gulfPhosphate,
  className,
}: {
  pampagro: ReputationDelta | null;
  gulfPhosphate: ReputationDelta | null;
  className?: string;
}) {
  const t = useTranslations("t3Scenario");
  return (
    <div className={clsx("flex h-full flex-col gap-3", className)}>
      {/* Full-width header banner — keeps the "trade closed" hero moment.   */}
      <div className="glass relative overflow-hidden p-4">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-financed to-transparent" />
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("revealKicker")}</p>
        <h2 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">
          {t("revealTitle")}
        </h2>
        <p className="mt-1 text-[11px] text-orbitra-teal-soft">
          {t("revealSummary")}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <BankBadge name="Itau" fundingPct={100} variant="edge" />
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
            {t("revealLcBadge")}
          </span>
        </div>
      </div>

      {/* Body split 2-col so the cumulative T1+T2+T3 strip + time-elapsed
       *  fit side-by-side with the reputation arc + dossier — eliminates
       *  the bottom overflow that clipped the cumulative stats on the
       *  fixed-height scene frame. */}
      <div className="grid flex-1 min-h-0 grid-cols-3 gap-3">
        {/* LEFT (col-span 2): reputation panes side-by-side + dossier */}
        <div className="col-span-2 flex min-h-0 flex-col gap-3">
          <div className="grid flex-1 min-h-0 grid-cols-2 gap-3">
            <ReputationPane
              name={t("revealPanePampagroName")}
              monogramName="Pampagro"
              flag="BR"
              headline={t("revealPaneHeadline")}
              before={pampagro?.before ?? { verifiedTrades: 22 }}
              after={pampagro?.after   ?? { verifiedTrades: 23, lifetimeVolumeUsd: 78_600_000, bankPartnersList: ["Itaú Unibanco", "Santander Brasil"] }}
              arc={t("revealPaneArcPampagro")}
              showBankPartner
            />
            <ReputationPane
              name={t("revealPaneGulfName")}
              monogramName="Gulf Phosphate"
              flag="AE"
              headline={t("revealPaneHeadline")}
              before={gulfPhosphate?.before ?? { verifiedTrades: 18 }}
              after={gulfPhosphate?.after   ?? { verifiedTrades: 19, lifetimeVolumeUsd: 50_400_000 }}
              arc={t("revealPaneArcGulf")}
            />
          </div>
          <TradeDossier scenarioId="t3-reverse-corridor" />
        </div>

        {/* RIGHT (col-span 1): time-elapsed (compact) + cumulative T1+T2+T3
         *  as a vertical 1-col list so the five stats stay readable in the
         *  narrower right column. */}
        <div className="flex min-h-0 flex-col gap-3">
          <div className="glass flex flex-col gap-1 px-3 py-2 text-[11px]">
            <span className="text-orbitra-paper">
              {t("revealTimeElapsedLabel")} <span className="font-mono text-state-traced">{t("revealTimeElapsedValue")}</span>
            </span>
            <span className="text-orbitra-muted">
              {t("revealPreOrbitraLabel")} <span className="font-mono">{t("revealPreOrbitraValue")}</span>
            </span>
            <span className="text-orbitra-muted">
              {t("revealItauIntegrationLabel")} <span className="font-mono">{t("revealItauIntegrationValue")}</span>
            </span>
          </div>

          {/* Cumulative T1 + T2 + T3 strip — vertical layout in the right
           *  column. Locks the "same engine, three regimes" thesis. */}
          <div className="glass relative flex-1 min-h-0 overflow-hidden p-3">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-state-verified via-state-traced to-state-financed opacity-60" />
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("revealCumulativeKicker")}
            </p>
            <div className="mt-2 flex flex-col gap-1.5 text-[11px]">
              <CumulativeStat label={t("cumulativeStatTradesClosed")}         value="3" sub="T1 · T2 · T3" />
              <CumulativeStat label={t("cumulativeStatCorridorsServed")}      value="2" sub="BR → AE · AE → BR" />
              <CumulativeStat label={t("cumulativeStatFinancingStructures")}  value="3" sub="Escrow · partial LC · full LC" />
              <CumulativeStat label={t("cumulativeStatRegulatorEcosystems")}  value="5" sub="MOCCAE · ABCC · CDIAL · FUC · SGS" />
              <CumulativeStat label={t("cumulativeStatDisputes")}             value="0" sub="three-field reconciliation matched" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReputationPane({
  name,
  monogramName,
  flag,
  headline,
  before,
  after,
  arc,
  showBankPartner,
}: {
  name: string;
  monogramName: string;
  flag: string;
  headline: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  arc: string;
  showBankPartner?: boolean;
}) {
  const t = useTranslations("t3Scenario");
  return (
    <div className="glass flex min-h-0 flex-col p-4">
      <div className="flex items-center gap-3">
        <Monogram name={monogramName} flag={flag} size={36} accent="var(--state-verified)" />
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{headline}</p>
          <p className="mt-0.5 font-display text-[15px] font-semibold text-orbitra-paper">{name}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <Stat label={t("statVerifiedTrades")}    beforeV={fmt(before.verifiedTrades)}       afterV={fmt(after.verifiedTrades)} />
        <Stat label={t("statLifetimeVolume")}    beforeV={fmtUsd(before.lifetimeVolumeUsd)} afterV={fmtUsd(after.lifetimeVolumeUsd)} />
        <Stat label={t("statOnTimeSettlement")}  beforeV={fmtPct(before.onTimePct)}         afterV={fmtPct(after.onTimePct)} />
        <Stat label={t("statBankFinancedShare")} beforeV={fmtPct(before.bankFinancedPct)}   afterV={fmtPct(after.bankFinancedPct)} />
      </div>

      {showBankPartner && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-1.5 text-[11px] text-state-financed">
          <span className="font-mono text-[10px] uppercase tracking-wider">{t("statBankPartnersOnFile")}</span>
          <BankBadge name="Itau" variant="compact" />
        </div>
      )}

      <div className="mt-auto rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-1.5 text-[11px] text-state-financed">
        {t("reputationArcPrefix")}<span className="font-mono">{arc}</span>
      </div>
    </div>
  );
}

function Stat({ label, beforeV, afterV }: { label: string; beforeV: string; afterV: string }) {
  const changed = beforeV !== afterV;
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={clsx("font-mono text-[11px]", changed ? "text-orbitra-muted line-through" : "text-orbitra-paper")}>{beforeV}</span>
        {changed && <span className="font-mono text-[10px] text-state-financed">→</span>}
        {changed && <span className="font-display text-base font-semibold text-state-financed">{afterV}</span>}
      </div>
    </div>
  );
}

function CumulativeStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="font-display text-2xl font-semibold leading-none text-state-financed">{value}</p>
      <div className="flex min-w-0 flex-col">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-paper">{label}</p>
        <p className="truncate text-[9px] text-orbitra-muted">{sub}</p>
      </div>
    </div>
  );
}

function fmt(v: unknown): string { return v === undefined || v === null ? "—" : String(v); }
function fmtUsd(v: unknown): string {
  if (v === undefined || v === null) return "—";
  const n = Number(v); if (!Number.isFinite(n)) return String(v);
  if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `USD ${(n / 1_000).toFixed(0)}K`;
  return `USD ${n}`;
}
function fmtPct(v: unknown): string { return v === undefined || v === null ? "—" : `${v}%`; }
